import React, { useState } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../firebase-config";
import { signInWithCustomTokenHandler } from "../AuthCustomToken";
import { getShortLivedToken } from "../customToken";
import { postRequest } from "../apiWrapper";
import getBrandFromHostname from "../utils/getBrandFromHostname";
import { HOSTS } from "../constants/hosts";
import { FaShieldAlt } from "react-icons/fa";

const AdminLoginPage = ({ onLogin }) => {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const queryParams = new URLSearchParams(window.location.search);
  const redirectTo = queryParams.get("redirectTo") || "report";
  const redirectQuery = redirectTo ? `?redirectTo=${redirectTo}` : "";

  // テーマ設定
  const themeConfig = {
    report: {
      bgColor: "bg-white",
      primaryColor: "text-blue-900",
      buttonBg: "bg-blue-600 hover:bg-blue-700",
      linkColor: "text-blue-600 hover:text-blue-800",
      headerText: "レポート管理者ログイン",
    },
    schedule: {
      bgColor: "bg-white",
      primaryColor: "text-green-900",
      buttonBg: "bg-green-600 hover:bg-green-700",
      linkColor: "text-green-600 hover:text-green-800",
      headerText: "スケジュール管理者ログイン",
    },
  };

  const theme = themeConfig[redirectTo] || themeConfig.report;

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // 🔹 Firebase Authentication でログイン
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      const idTokenResult = await user.getIdTokenResult();

      // JWT トークンを取得
      const token = await user.getIdToken();

      if (false) {
        // 短命カスタムトークンを取得
        const customToken = await getShortLivedToken(user.uid);
        if (customToken) {
          console.log("カスタムトークンを取得しました");
          // カスタムトークンでログイン
          const newUser = await signInWithCustomTokenHandler(customToken);
          if (newUser) {
            console.log("カスタムトークンを保存");
            const newToken = await newUser.getIdToken();
            sessionStorage.setItem("jwtToken", newToken);
          }
        }
      }

      const role = idTokenResult.claims.role;

      if (!role) {
        throw new Error("ロール情報が取得できません");
      }

      if (role === "teacher") {
        throw new Error("教師アカウントは別のログインページを使用してください");
      }

      if (role !== "admin") {
        throw new Error("許可されていないログインです");
      }

      // カスタムトークン取得
      const uid = user.uid;
      const response = await postRequest("generate-token", { uid });

      const data = await response.json();
      if (!data.customToken) {
        throw new Error("カスタムトークン取得失敗:", data.error);
      }

      // onLogin コールバックを使用
      onLogin(role, email);

      // HOSTSキーの組み立て
      const redirectKey = `admin${redirectTo.charAt(0).toUpperCase()}${redirectTo.slice(1)}`;

      // HOSTSに存在しないキーだったらデフォルトに戻す
      const targetHost = HOSTS[redirectKey] || HOSTS.adminReport;

      window.location.href = `https://${targetHost}.${getBrandFromHostname()}.classora.jp?token=${data.customToken}`;
    } catch (error) {
      console.error("❌ 管理者ログインエラー:", error);
      setErrorMessage("ログインに失敗しました。メールアドレスまたはパスワードを確認してください。");
    } finally {
      setLoading(false);
    }
  };

  const AppSwitcher = () => {
    return (
      <div className="flex mb-6">
        <a
          href="?redirectTo=report"
          className={`px-4 py-2 border rounded-l ${redirectTo === "report"
            ? "bg-white font-bold"
            : "bg-gray-200 hover:bg-gray-300"
            }`}
        >
          レポート
        </a>
        <a
          href="?redirectTo=schedule"
          className={`px-4 py-2 border-t border-b border-r rounded-r ${redirectTo === "schedule"
            ? "bg-white font-bold"
            : "bg-gray-200 hover:bg-gray-300"
            }`}
        >
          スケジュール
        </a>
      </div>
    );
  };

  return (
    <div className={`flex flex-col items-center justify-center h-screen ${theme.bgColor}`}>
      {/* 管理者専用表示 */}
      <div className="flex items-center space-x-3 mb-2">
        <FaShieldAlt className={`${theme.primaryColor} text-2xl`} />
        <span className={`text-lg font-semibold ${theme.primaryColor}`}>管理者専用ページ</span>
      </div>

      <h1 className={`text-3xl font-bold mb-4 ${theme.primaryColor}`}>{theme.headerText}</h1>

      <AppSwitcher />

      <form className="bg-white p-6 rounded-lg shadow-md w-96 border border-gray-200" onSubmit={handleLogin}>
        <div className="mb-4">
          <label className="block text-sm font-semibold mb-2 text-gray-700">メールアドレス</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300"
            placeholder="メールアドレスを入力"
            required
          />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-semibold mb-2 text-gray-700">パスワード</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300"
            placeholder="パスワードを入力"
            required
          />
        </div>
        {errorMessage && <p className="text-red-500 text-sm mb-4">{errorMessage}</p>}
        <button
          type="submit"
          className={`w-full ${theme.buttonBg} text-white py-2 rounded-lg transition`}
          disabled={loading}
        >
          {loading ? "ログイン中..." : "ログイン"}
        </button>
        <div className="mt-4 text-center">
          <a
            href={`https://${HOSTS.teacherLogin}.${getBrandFromHostname()}.classora.jp${redirectQuery}`}
            className={`transition ${theme.linkColor}`}
          >
            講師用のページはこちら
          </a>
        </div>
        <div className="mt-2 text-sm text-gray-500 text-center">
          ※ 教室の管理者アカウント専用です。講師の方は上記リンクをご利用ください。
        </div>
      </form>
    </div>
  );
};

export default AdminLoginPage;
